import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Alert,
  Button
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, Error as ErrorIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const ShopifyCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  
  const { saveShopifyCredentials } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    const shop = searchParams.get('shop');
    const code = searchParams.get('code');
    
    if (!shop || !code) {
      setStatus('error');
      setErrorMessage('Shop or code parameter is missing');
      return;
    }
    
    const handleAuth = async () => {
      try {
        // Exchange the authorization code for an access token via your backend
        const response = await api.post('/api/shopify/auth/exchange-token', {
          shop,
          code
        });
        
        if (response.data.access_token) {
          // Save the credentials
          await saveShopifyCredentials(shop, response.data.access_token);
          setStatus('success');
          
          // Auto-redirect after success
          setTimeout(() => {
            navigate('/');
          }, 3000);
        } else {
          throw new Error('Access token not received');
        }
      } catch (error) {
        console.error('Shopify authentication failed:', error);
        setStatus('error');
        setErrorMessage(error instanceof Error ? error.message : 'Authentication failed');
      }
    };
    
    handleAuth();
  }, [searchParams, saveShopifyCredentials, navigate]);
  
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: '#f5f5f5'
      }}
    >
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          maxWidth: 500, 
          width: '100%',
          borderRadius: 2,
          textAlign: 'center'
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom>
          Shopify Authentication
        </Typography>
        
        {status === 'loading' && (
          <Box sx={{ my: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="body1">
              Processing your authentication...
            </Typography>
          </Box>
        )}
        
        {status === 'success' && (
          <Box sx={{ my: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="body1" gutterBottom>
              Successfully connected to Shopify!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Redirecting you to the dashboard in a few seconds...
            </Typography>
          </Box>
        )}
        
        {status === 'error' && (
          <Box sx={{ my: 4 }}>
            <Alert 
              severity="error" 
              icon={<ErrorIcon fontSize="large" />}
              sx={{ mb: 3 }}
            >
              <Typography variant="subtitle1" gutterBottom>
                Authentication Failed
              </Typography>
              <Typography variant="body2">
                {errorMessage || 'An unexpected error occurred during authentication.'}
              </Typography>
            </Alert>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                For Shopify integration, you may need to enter your Shop URL and Access Token manually in the settings.
              </Typography>
              
              <Button
                variant="contained"
                onClick={() => navigate('/settings')}
                fullWidth
              >
                Go to Settings
              </Button>
            </Box>
          </Box>
        )}
        
        {status === 'success' && (
          <Button
            variant="outlined"
            onClick={() => navigate('/')}
            sx={{ mt: 2 }}
          >
            Go to Dashboard Now
          </Button>
        )}
      </Paper>
    </Box>
  );
};

export default ShopifyCallbackPage;